<template>
  <div class="c-section-management w-full h-full">
    <template v-if="tableRef">
      <CBulkToolbar
        v-show="tableRef?.selectedRowsCount"
        class="min-h[68px] px-5"
        :count="tableRef?.pageRowsCount"
        :disabled="isBulkToolbarDisabled"
        :moreActions="{
          addTags: true,
          removeTags: true,
          delete: true,
          manageAttributes: client === CLIENT_TYPE.admin,
          manageDistribution: client === CLIENT_TYPE.admin && isDistributionEnabled,
        }"
        :selectedCount="tableRef.selectedRowsCount"
        showDelete
        showMore
        @addTags="bulkAddTags"
        @delete="bulkDelete"
        @manageAttributes="bulkUpdateMetadata"
        @manageDistribution="onBulkDistribution"
        @removeTags="bulkRemoveTags"
        @toggleSelect="tableRef.selectAll"
      />
      <CSectionManagementToolbar
        v-show="!tableRef?.selectedRowsCount"
        ref="toolbarWrapper"
        :custom-filter-components="dataFilterComponents"
        :hide-create="isImpact || areSectionsSystemControlled"
        :is-loading-items="isLoading"
        :isViewHidden="!launchDarkly.allow_canvases_tables_columns_settings"
        :metadata-filter-values="filters"
        :recommended-section-ids="activeRecommendedSectionIds"
        :search="search"
        :tableColumnsSettings="tableColumnsSettings"
        @create:section="emit('create')"
        @move="onColumnsMove"
        @restoreColumns="restoreColumns"
        @search="onTableChange('searchChanged', $event)"
        @select="onColumnsSelect"
        @update:metadata-filter-values="onTableChange('metadataFilterValuesChanged', $event)"
      />
    </template>
    <template v-if="shouldShowSmartFolderView">
      <CSectionSmartFolders
        class="h-full"
        mode="management"
        :storage-key="storageKey"
        @edit-section="emit('edit', $event)"
        @open-section="emit('open', $event)"
      />
    </template>
    <CTable
      v-else
      ref="tableRef"
      v-model:selected-rows="selectedRows"
      class="mt-3"
      :columns="launchDarkly.allow_canvases_tables_columns_settings ? columnsFromSettings : defaultColumns"
      :data="filteredAndSortedData"
      :initialSorting="[{ id: 'modified_at', desc: true }]"
      :loading="isLoadingTable"
      :page-index="fetchingParams.page - 1"
      :page-size="fetchingParams.page_size"
      :page-sizes="DEFAULT_PAGE_SIZE_OPTIONS"
      :scroller-style="{ height: `calc(100vh - 320px - ${sectionManagementToolbarHeight || 0}px)` }"
      :selectable="launchDarkly.allow_bulk_actions_canvases_files"
      server-side
      :total-count="totalCount"
      @page-changed="onTableChange('pageChanged', $event)"
      @page-size-changed="onTableChange('pageSizeChanged', $event)"
      @sort-changed="onTableChange('sortChanged', $event)"
    />
    <slot />
  </div>
</template>
<script setup lang="tsx">
import { h, provide, ref, onMounted, computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { createColumnHelper, type ColumnDef, type SortingState, type PaginationState } from '@tanstack/table-core'
import { isEqual, omit, orderBy, unionBy } from 'lodash-es'

import CTable from '@lib/components/CTable/CTable.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import CSectionManagementToolbar from './CSectionManagementToolbar.vue'
import CSectionManagementActionsCell from './CSectionManagementActionsCell.vue'
import CCanvasDeleteDialogContent from '@lib/components/canvases/CCanvasDeleteDialogContent.vue'
import CBulkToolbar from '@lib/components/CBulkToolbar/CBulkToolbar.vue'
import CPopularityAndEngagementIcons from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementIcons.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import { usePopularity, EntityType } from '@canvas-builder/composables/usePopularity'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useThemeVars from '@lib/composables/useThemeVars'
import type { PaginatedData } from '@lib/types/paginatedData'
import { CLIENT_TYPE } from '@lib/constants/cdp.const'
import type { GetCanvasesParams } from '@lib/types/canvases'
import { formatDateTime } from '@lib/util/date'
import useCanvasesManagementBulk from '@lib/components/canvases/CanvasesManagementBulk.use.tsx'
import { isMac, isWindows, isPitcherWkWebView } from '@lib/util/userAgents'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'
import type {
  CanvasRetrieve,
  CanvasCreateRequest,
  PatchedCanvasUpdateRequest,
  PaginatedMetadataTemplateList,
} from '@openapi'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import { useElementSize } from '@vueuse/core'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { DEFAULT_PAGE_SIZE_OPTIONS } from '@lib/constants/tables.const'
import useToast from '@lib/composables/useToast'
import { useTableColumnsSettings } from '@lib/composables/tableColumnsSettings.use'
import usePagination from '@lib/composables/useCanvasTablesPagination'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import CSwitch from '@lib/components/CSwitch/CSwitch.vue'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import useSectionSmartFolderStore from '@lib/stores/sectionSmartFolderStore'
import CSectionSmartFolders from '@lib/components/CSectionSmartFolders/CSectionSmartFolders.vue'
import { useCanvasStandardFilters } from '@lib/composables/useCanvasStandardFilters'
import type { PitcherInfo } from '@lib/types/app'
import CContentError from '@lib/components/CContentError/CContentError.vue'

const DEFAULT_ORDERING = '-modified_at'

const props = defineProps<{
  storageKey: string
  myUserId?: CanvasRetrieve['created_by']['id']
  updater: (
    params: PatchedCanvasUpdateRequest & { id: CanvasRetrieve['id']; fields?: string },
  ) => Promise<CanvasRetrieve>
  creator: (params: Omit<CanvasCreateRequest, 'instance_id'>) => Promise<CanvasRetrieve>
  fetcher: (
    params: GetCanvasesParams & { filters?: Record<string, any>; fields?: string; id__in?: string },
  ) => Promise<PaginatedData<CanvasRetrieve>>
  deleter: (id: CanvasRetrieve['id']) => Promise<CanvasRetrieve>
  byIdFetcher: (params: { id: CanvasRetrieve['id']; fields?: string }) => Promise<CanvasRetrieve>
  client: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
  areSectionsSystemControlled?: boolean
}>()

const emit = defineEmits<{
  open: [canvas: CanvasRetrieve, newTab?: boolean]
  details: [canvas: CanvasRetrieve]
  edit: [canvas: CanvasRetrieve]
  create: []
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)

const { t } = useI18n()
const { toast } = useToast()
const themeVars = useThemeVars()
const confirmation = useConfirmation()
const { fetchSuggestedInstanceTags } = useSuggestedTags()
const {
  bulkDeleteDialog,
  bulkAddTagsDialog,
  bulkRemoveTagsDialog,
  bulkUpdateMetadataDialog,
  bulkUpdateDistributionDialog,
} = useCanvasesManagementBulk()
const { fetchPopularityData } = usePopularity()
const { getOrderingConfiguration, getSystemFilterSectionIds, popularityDict } = useSectionPopularity(
  computed(() => filters.value),
)
const { recommendedSectionIds } = useRecommendations()
const { isSmartFoldersView } = useSectionSmartFolderStore()

const metadataFetcher = inject<
  (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
>('instanceMetadataTemplatesFetcher')

const tableRef = ref<InstanceType<typeof CTable> | null>(null)
const toolbarWrapper = ref<HTMLElement | null>(null)
const selectedRows = ref<Record<string, boolean>>({})
const isCopyingId = ref<CanvasRetrieve['id']>('')
const deletingInProgress = ref<Set<CanvasRetrieve['id']>>(new Set())
const editingColumn = ref<{ id: CanvasRetrieve['id']; column: string } | null>(null)
const fetchedData = ref<CanvasRetrieve[]>([])
const isLoading = ref(false)
const isBulkDeleting = ref(false)
const isBulkUpdating = ref(false)
const totalCount = ref(0)

const initialFilters: Record<string, any> = {}

const {
  search,
  page,
  pageSize,
  selectedFilters: filters,
  ordering,
} = usePagination({
  storageKey: props.storageKey,
  initialFilters,
  initialOrdering: DEFAULT_ORDERING,
})

const fetchingParams = computed(() => ({
  page_size: pageSize.value,
  page: page.value,
  ordering: ordering.value,
  filters: filters.value,
  search: search.value,
}))

const { height: sectionManagementToolbarHeight } = useElementSize(toolbarWrapper)

const isImpact = computed(() => props.client === CLIENT_TYPE.impact)

const { filterComponents: dataFilterComponents } = useCanvasStandardFilters({
  includeTemplateFilter: false,
  includeDraftFilter: pitcherInfo.value?.isAnyTypeOfAdmin ?? false,
  includeOwnerFilter: !isImpact.value,
})
const isDistributionEnabled = computed(() => launchDarkly.value?.enable_canvas_core_distributions)

const activeRecommendedSectionIds = computed(() =>
  filteredAndSortedData.value.filter((item) => recommendedSectionIds.value.includes(item.id)).map((item) => item.id),
)

const filteredAndSortedData = computed(() => {
  const sorters = getOrderingConfiguration()

  return orderBy(
    filteredData.value,
    sorters.map(([sorter]) => sorter),
    sorters.map(([, order]) => order),
  )
})

provide('refetchSections', () => onTableChange('itemsChanged'))

const fetchFields = [
  'access_type',
  'id',
  'name',
  'tags',
  'is_draft',
  'modified_at',
  'created_by',
  'metadata',
  'thumbnail_url',
  'is_section',
  'is_block',
  'is_template',
  'folder_id',
  ...(props.client === CLIENT_TYPE.admin ? ['modified_by'] : []),
]

const isBulkToolbarDisabled = computed(
  () => !!isCopyingId.value || deletingInProgress.value.size > 0 || isBulkDeleting.value || isBulkUpdating.value,
)

const isLoadingTable = computed(() => isLoading.value || isBulkDeleting.value)

const filteredData = computed(() => fetchedData.value) // turn off the extra client side filtering

const shouldShowSmartFolderView = computed(
  () => launchDarkly.value.enable_smart_folders_for_sections && isSmartFoldersView.value,
)

async function updateProperty<T extends keyof CanvasRetrieve>(
  item: CanvasRetrieve,
  property: T,
  newValue: CanvasRetrieve[T],
) {
  try {
    const updatedItem = await props.updater({
      id: item.id,
      [property]: newValue,
      fields: fetchFields.join(','),
    })

    const foundIndex = fetchedData.value.findIndex((section) => section.id === item.id)
    if (foundIndex !== -1) {
      fetchedData.value = [
        ...fetchedData.value.slice(0, foundIndex),
        updatedItem,
        ...fetchedData.value.slice(foundIndex + 1),
      ]
    }
  } catch (e) {
    console.error('section update failed:', e)
  }
}

async function deleteCanvas(item: CanvasRetrieve) {
  confirmation.danger({
    hasExit: false,
    title: t('canvasUI.components.sections.CSectionManagement.deleteDialog.title'),
    content: () =>
      h(CCanvasDeleteDialogContent, {
        name: item?.name,
        texts: {
          name: t('canvasUI.components.sections.CSectionManagement.deleteDialog.content.name'),
          info: t('canvasUI.components.sections.CSectionManagement.deleteDialog.content.info'),
        },
      }),
    confirmText: t('canvasUI.components.sections.CSectionManagement.deleteDialog.confirm'),
    cancelText: t('canvasUI.components.sections.CSectionManagement.deleteDialog.cancel'),
    onConfirm: async () => {
      try {
        deletingInProgress.value.add(item.id)
        await props.deleter(item.id)
        onTableChange('itemsChanged')

        toast.success(
          t('canvasUI.components.sections.CSectionManagement.notifications.delete.success', {
            name: item.name,
          }),
        )
      } catch (e) {
        console.error('section deletion failed:', e)

        toast.error(
          t('canvasUI.components.sections.CSectionManagement.notifications.delete.error', {
            name: item.name,
          }),
        )
      } finally {
        deletingInProgress.value.delete(item.id)
      }
    },
  })
}

async function bulkDelete() {
  bulkDeleteDialog({
    selectedRows,
    filteredData,
    isBulkDeleting,
    canvasType: 'section',
    isReadonly: isReadonlyBulk,
    onComplete: () => {
      onTableChange('itemsChanged')
    },
  })
}

async function bulkAddTags() {
  bulkAddTagsDialog({
    selectedRows,
    filteredData,
    isBulkUpdating,
    canvasType: 'section',
    isReadonly: isReadonlyBulk,
    onComplete: () => {
      onTableChange('itemsChanged')
    },
  })
}

async function bulkRemoveTags() {
  bulkRemoveTagsDialog({
    selectedRows,
    filteredData,
    isBulkUpdating,
    canvasType: 'section',
    isReadonly: isReadonlyBulk,
    onComplete: () => {
      onTableChange('itemsChanged')
    },
  })
}

async function bulkUpdateMetadata() {
  bulkUpdateMetadataDialog({
    selectedRows,
    selectedItemNames: filteredData.value?.filter((item) => selectedRows.value[item.id]).map((item) => item.name),
    metadataFetcher,
    updater: props.updater,
    onComplete: () => {
      onTableChange('itemsChanged')
    },
    isBulkUpdating,
    canvasType: 'section',
  })
}

async function copyCanvas(item: CanvasRetrieve) {
  try {
    isCopyingId.value = item.id
    let copyIdx = 1
    let copyName = `${item.name} - ${t('canvasUI.components.sections.CSectionManagement.copy')}_${copyIdx}`
    while (fetchedData.value.some((canvas) => canvas.name === copyName)) {
      copyIdx++
      copyName = `${item.name} - ${t('canvasUI.components.sections.CSectionManagement.copy')}_${copyIdx}`
    }
    const { id, is_default, owned_by, created_by, ...toCopyFrom } = await props.byIdFetcher({
      id: item.id,
    })
    await props.creator({
      ...toCopyFrom,
      name: copyName,
      tags: toCopyFrom.tags ?? [],
      is_section: true,
    })
    onTableChange('itemsChanged')

    toast.success(
      t('canvasUI.components.sections.CSectionManagement.notifications.duplicate.success', {
        name: item.name,
      }),
    )
  } catch (e) {
    console.error('section copy failed:', e)

    toast.error(
      t('canvasUI.components.sections.CSectionManagement.notifications.duplicate.error', {
        name: item.name,
      }),
    )
  } finally {
    isCopyingId.value = ''
  }
}

function isReadonly(item: CanvasRetrieve) {
  return (isImpact.value && item.created_by?.id !== props.myUserId) || !!editingColumn.value?.id
}

function isReadonlyBulk(item: CanvasRetrieve) {
  return isImpact.value && item.created_by?.id !== props.myUserId
}

function isEditingCurrentColumn(id: CanvasRetrieve['id'], column: string) {
  return editingColumn.value?.id === id && editingColumn.value?.column === column
}

function clearEditingColumn() {
  editingColumn.value = null
}

const columnHelper = createColumnHelper<CanvasRetrieve>()

const defaultColumns = [
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    cell: (cellCtx) =>
      h(
        CTableInput,
        {
          modelValue: cellCtx.getValue(),
          state: isEditingCurrentColumn(cellCtx.row.original.id, cellCtx.column.id) ? 'edit' : 'view',
          readonly: isReadonly(cellCtx.row.original),
          valueClass: 'font-700 underline unerline-solid',
          class: 'cursor-pointer',
          'onUpdate:modelValue': (newValue) => {
            updateProperty(cellCtx.row.original, 'name', newValue)
            clearEditingColumn()
          },
          onClick: (e: MouseEvent) => {
            const newTab = (isWindows && e.ctrlKey) || (isMac && e.metaKey)
            emit('open', cellCtx.row.original, newTab)
          },
          onEdit: () => {
            editingColumn.value = {
              id: cellCtx.row.original.id,
              column: cellCtx.column.id,
            }
          },
          onCancel: clearEditingColumn,
        },
        {
          prefix: () => {
            const thumbnailUrl = cellCtx.row.original.thumbnail_url ?? cellCtx.row.original.metadata?.thumbnail

            return (
              <CThumbnail
                class="mr-2"
                thumbnailUrl={thumbnailUrl}
                item={cellCtx.row.original}
                themeVars={themeVars.value}
                enableThumbnails={true}
              />
            )
          },
          suffix: () =>
            h(
              'div',
              {
                class: 'flex items-center gap-2 ml-4',
              },
              [
                cellCtx.row.original?.access_type === 'restricted' &&
                  h(CContentError, {
                    onlyIcon: true,
                    restricted: true,
                  }),
                h(CPopularityAndEngagementIcons, {
                  class: 'flex gap-2',
                  isRecommended: recommendedSectionIds.value.includes(cellCtx.row.original.id),
                  popularity: launchDarkly.value.show_popularity_icons
                    ? popularityDict.value[cellCtx.row.original.id]?.popularity
                    : undefined,
                  engagement: launchDarkly.value.show_popularity_icons
                    ? popularityDict.value[cellCtx.row.original.id]?.engagement
                    : undefined,
                  views: launchDarkly.value.show_popularity_icons
                    ? (popularityDict.value[cellCtx.row.original.id]?.views ?? 0)
                    : 0,
                }),
              ],
            ),
        },
      ),
    header: t('canvasUI.components.sections.CSectionManagement.nameTh'),
  }),
  columnHelper.accessor((row) => row.tags, {
    id: 'tags',
    cell: (cellCtx) =>
      h(CTableTag, {
        modelValue: cellCtx.getValue() ?? [],
        state: isEditingCurrentColumn(cellCtx.row.original.id, cellCtx.column.id) ? 'edit' : 'view',
        readonly: isReadonly(cellCtx.row.original),
        rowIndex: cellCtx.row.index,
        suggestedTagsFetcher: (search) =>
          fetchSuggestedInstanceTags(search, { type: ['canvases'], canvasType: 'section' }),
        'onUpdate:modelValue': (newValue) => {
          updateProperty(cellCtx.row.original, 'tags', newValue)
          clearEditingColumn()
        },
        onEdit: () => {
          editingColumn.value = {
            id: cellCtx.row.original.id,
            column: cellCtx.column.id,
          }
        },
        onCancel: clearEditingColumn,
      }),
    header: t('canvasUI.components.sections.CSectionManagement.tagsTh'),
    enableSorting: false,
    meta: {
      classes: launchDarkly.value.enable_short_tag_column_in_tables ? '!max-w-[200px]' : undefined,
    },
  }),
  columnHelper.accessor((row) => row.is_draft, {
    id: 'is_draft',
    cell: (cellCtx) => (
      <div class="flex items-center">
        <CSwitch
          checked={!cellCtx.getValue()}
          disabled={isReadonly(cellCtx.row.original)}
          onChange={async (newValue: boolean) => await updateProperty(cellCtx.row.original, 'is_draft', !newValue)}
          themeOverrides={{
            railColorActive: themeVars.value.success,
            railColor: themeVars.value.primary6,
          }}
        />
      </div>
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagementStatusCell.status.ready'),
    meta: {
      classes: 'w-[110px]',
    },
  }),
  columnHelper.accessor((row) => row.modified_at, {
    id: 'modified_at',
    cell: (cellCtx) => h('div', { class: 'min-w-[150px]' }, formatDateTime(cellCtx.getValue()) as string),
    header: t('canvasUI.components.sections.CSectionManagement.modifiedAtTh'),
  }),
  ...(props.client === CLIENT_TYPE.admin //TODO: Server side sorting only for names, emails cannot be sorted
    ? [
        columnHelper.accessor((row) => row.created_by, {
          id: 'owned_by__name',
          cell: (cellCtx) => {
            const createdBy = cellCtx.row.original.created_by.name || cellCtx.row.original.created_by.email || ''
            return h('div', { class: 'min-w-[150px]' }, createdBy)
          },
          header: t('canvasUI.components.sections.CSectionManagement.createdByTh'),
          meta: {
            classes: 'min-w-[150px]',
          },
        }),
        columnHelper.accessor((row) => row.modified_by.name || row.modified_by.email, {
          id: 'modified_by__name',
          cell: (cellCtx) => h('div', { class: 'min-w-[150px]' }, cellCtx.getValue()),
          header: t('canvasUI.components.sections.CSectionManagement.modifiedByTh'),
          meta: {
            classes: 'min-w-[150px]',
          },
        }),
      ]
    : []),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    cell: (cellCtx) =>
      h(CSectionManagementActionsCell, {
        hideCopy: isImpact.value || props.areSectionsSystemControlled,
        hideViewDetails: isImpact.value,
        hideViewNewTab: isPitcherWkWebView,
        hideEdit: isReadonly(cellCtx.row.original),
        hideDelete: isReadonly(cellCtx.row.original) || props.areSectionsSystemControlled,
        isCopying: isCopyingId.value === cellCtx.row.original.id,
        isDeleting: deletingInProgress.value.has(cellCtx.cell.row.original.id),
        onCopy: () => copyCanvas(cellCtx.cell.row.original),
        onEdit: () => emit('edit', cellCtx.row.original),
        onDelete: () => deleteCanvas(cellCtx.cell.row.original),
        onView: () => emit('open', cellCtx.row.original),
        onViewNewTab: () => emit('open', cellCtx.cell.row.original, true),
        onViewDetails: () => emit('details', cellCtx.row.original),
      }),
    header: t('canvasUI.components.sections.CSectionManagement.actionsTh'),
    enableSorting: false,
  }),
] as ColumnDef<Record<string, any>, any>[]

const TABLE_COLUMNS_SETTINGS_KEY = 'ciq_sections_management_table_columns_settings'

const { tableColumnsSettings, columnsFromSettings, onColumnsMove, onColumnsSelect, restoreColumns } =
  useTableColumnsSettings(TABLE_COLUMNS_SETTINGS_KEY, defaultColumns)

function onTableChange(
  event:
    | 'pageChanged'
    | 'pageSizeChanged'
    | 'sortChanged'
    | 'searchChanged'
    | 'itemsChanged'
    | 'metadataFilterValuesChanged',
  state?: number | SortingState | PaginationState | string | Record<string, any>,
) {
  const initialParams = { ...fetchingParams.value }
  let change
  switch (event) {
    case 'pageChanged':
      change = state as PaginationState
      page.value = change.pageIndex + 1
      break
    case 'pageSizeChanged':
      change = state as number
      pageSize.value = change ?? fetchingParams.value.page_size
      page.value = 1
      break
    case 'sortChanged':
      ;[change] = state as SortingState
      ordering.value = change?.desc ? '-' + change.id : (change?.id ?? DEFAULT_ORDERING)
      page.value = 1
      break
    case 'searchChanged':
      change = state as string
      search.value = change
      page.value = 1
      break
    case 'itemsChanged':
      page.value = 1
      break
    case 'metadataFilterValuesChanged':
      change = state as Record<string, any>
      filters.value = change
      page.value = 1
      break
  }

  clearEditingColumn()

  if (event === 'itemsChanged' || !isEqual(initialParams, fetchingParams.value)) {
    selectedRows.value = {}
    fetch(fetchingParams.value)
  }
}

async function fetch(
  params: {
    page_size: number
    page: number
    ordering: string
    search: string
    filters: Record<string, any>
  },
  force = false,
) {
  try {
    isLoading.value = true
    const requestParams = params.search ? params : omit(params, 'search')

    const sectionIds = getSystemFilterSectionIds()

    // only check for null value not empty array
    if (!sectionIds && !force) {
      totalCount.value = 0
      isLoading.value = false
      return
    }

    // Separate data filters from metadata filters
    const dataFilters: Record<string, any> = {}
    const metaDataFilters: Record<string, any> = {}

    Object.entries(requestParams.filters).forEach(([key, value]) => {
      if (dataFilterComponents.value.some((component) => component.name === key)) {
        dataFilters[key] = value
      } else {
        metaDataFilters[key] = value
      }
    })

    const normalizedMetaDataFilters = normalizeFilterParams(metaDataFilters)

    const requestPromises = [
      props.fetcher({
        ...requestParams,
        filters: normalizedMetaDataFilters,
        ...dataFilters,
        is_section: true,
        is_template: false,
        fields: fetchFields.join(','),
        id__in: sectionIds?.length ? sectionIds.slice(0, requestParams.page_size).join(',') : undefined,
      }),
    ]

    // only show all recommended sections if on the first page
    if (recommendedSectionIds.value?.length && fetchingParams.value.page === 1 && !sectionIds?.length)
      requestPromises.push(
        props.fetcher({
          ...requestParams,
          filters: normalizedMetaDataFilters,
          ...dataFilters,
          is_section: true,
          is_template: false,
          fields: fetchFields.join(','),
          id__in: recommendedSectionIds.value.join(','),
        }),
      )

    const [sectionsResponse, recommendedSectionsResponse] = await Promise.all(requestPromises)

    if (recommendedSectionsResponse && recommendedSectionsResponse.results?.length) {
      totalCount.value = sectionsResponse.count

      const sectionsWithAllRecommendedSections = unionBy(
        sectionsResponse.results,
        recommendedSectionsResponse.results,
        'id',
      )

      fetchedData.value = sectionsWithAllRecommendedSections
    } else {
      totalCount.value = sectionsResponse.count
      fetchedData.value = sectionsResponse.results
    }
  } catch (e) {
    console.error(e)
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  try {
    isLoading.value = true
    await fetch(fetchingParams.value)
    if (launchDarkly.value.show_popularity_icons) {
      await fetchPopularityData(EntityType.SECTION)
    }
  } catch (e) {
    console.error(e)
    isLoading.value = false
  }
})

defineExpose({
  refetch: () => fetch(fetchingParams.value),
})

function onBulkDistribution() {
  bulkUpdateDistributionDialog({
    selectedCount: tableRef.value?.selectedRowsCount || 0,
    isBulkUpdating,
    canvasType: 'section',
    selectedRows,
    onComplete: () => {
      onTableChange('itemsChanged')
    },
  })
}
</script>
